import { useEffect, useRef, useState } from 'react';
import { FieldShell } from './FieldShell';
import { useDomFieldName, useDomFieldId } from './_useFieldName';
import { FIELD_AUTOCOMPLETE_OFF, FIELD_NO_AUTOFILL } from './_fieldDefaults';

const BLOCK = 'LStudio__app_field';

/*
    🔥 AUTOCOMPLETADO PROPIO

    Distinto de FieldSelectSearch: acá el valor es TEXTO LIBRE y las
    sugerencias son una ayuda, no una restricción. Sirve para ciudades,
    calles, marcas: el usuario puede escribir algo que no está en la lista.

    Sin librería. Navegación con flechas, Enter elige, Escape cierra.
*/

export const FieldAutocomplete = ({
    id, name, label, hint, error,
    value = '', onChange, options = [],
    placeholder = 'Escribí para buscar…',
    maxSuggestions = 6, minChars = 1,
    isRequired = false, isDisabled = false,
    span = 1,
}) => {

    const [useIsOpen, setIsOpen] = useState(false);
    const [useIndex, setIndex] = useState(-1);

    const REF__useRefBox = useRef(null);

    const MATCHES = String(value).length >= minChars
        ? options
            .filter((opt) => String(opt.label).toLowerCase().includes(String(value).toLowerCase()))
            .slice(0, maxSuggestions)
        : [];

    // 🔥 Cierra al clickear afuera
    useEffect(() => {

        const onClickOutside = (event) => {
            if (REF__useRefBox.current && !REF__useRefBox.current.contains(event.target)) setIsOpen(false);
        };

        document.addEventListener('mousedown', onClickOutside);
        return () => document.removeEventListener('mousedown', onClickOutside);

    }, []);

    const onPick = (label) => {
        onChange?.({ target: { name: name ?? id, value: label } });
        setIsOpen(false);
        setIndex(-1);
    };

    const onKeyDown = (event) => {

        if (!useIsOpen || !MATCHES.length) return;

        if (event.key === 'ArrowDown') {
            event.preventDefault();
            setIndex((i) => (i + 1) % MATCHES.length);
        }

        if (event.key === 'ArrowUp') {
            event.preventDefault();
            setIndex((i) => (i <= 0 ? MATCHES.length - 1 : i - 1));
        }

        if (event.key === 'Enter' && useIndex >= 0) {
            event.preventDefault();
            onPick(MATCHES[useIndex].label);
        }

        if (event.key === 'Escape') setIsOpen(false);
    };

    const DOM_NAME = useDomFieldName();

    const DOM_ID = useDomFieldId(id);

    return (
        <FieldShell id={DOM_ID} label={label} hint={hint} error={error}
            isRequired={isRequired} isDisabled={isDisabled} span={span} >

            <div className={`${BLOCK}__autocomplete`} ref={REF__useRefBox}>

                <input id={DOM_ID} data-field={name ?? id} name={DOM_NAME} type="text"
                    className={`${BLOCK}__input`}
                    value={value} placeholder={placeholder}
                    onChange={(event) => { onChange?.(event); setIsOpen(true); setIndex(-1); }}
                    onFocus={() => setIsOpen(true)} onKeyDown={onKeyDown}
                    required={isRequired} disabled={isDisabled}
                    autoComplete={FIELD_AUTOCOMPLETE_OFF}
                    aria-invalid={!!error} aria-expanded={useIsOpen} role="combobox"
                    {...FIELD_NO_AUTOFILL} />

                {
                    useIsOpen && MATCHES.length > 0 && (
                        <ul className={`${BLOCK}__suggestions`} role="listbox">
                            {
                                MATCHES.map((opt, i) => (
                                    <li key={opt.value} role="option" aria-selected={i === useIndex}
                                        className={`${BLOCK}__suggestion`} data-active={i === useIndex}
                                        onMouseEnter={() => setIndex(i)}
                                        onMouseDown={(event) => { event.preventDefault(); onPick(opt.label); }} >
                                        {opt.label}
                                    </li>
                                ))
                            }
                        </ul>
                    )
                }

            </div>

        </FieldShell>
    );
};
